import type { CardData, DataAdapter, PullOptions, RarityTier } from '@gachakit/core/types/adapter';
import { allFriendCards, friendPacks } from './cards';

const RARITY_WEIGHTS: Record<RarityTier, number> = {
  common: 48,
  uncommon: 28,
  rare: 15,
  epic: 7,
  legendary: 2
};

export class FriendPacksAdapter implements DataAdapter {
  id = 'mates-packs';
  name = 'Mates Gacha';
  description = 'A local friend-pack adapter for collecting the squad as shiny spell cards.';

  constructor(private readonly packId?: string) {}

  async pullRandom(options?: PullOptions): Promise<CardData> {
    const excluded = new Set(options?.excludeIds ?? []);
    const selectedPack = this.packId
      ? friendPacks.find((pack) => pack.id === this.packId)
      : undefined;
    const cardPool = selectedPack?.cards ?? allFriendCards;
    const available = cardPool.filter((card) => !excluded.has(card.id));
    const guaranteed = options?.guaranteeRarity
      ? available.filter((card) => card.rarity === options.guaranteeRarity)
      : [];
    const pool = guaranteed.length > 0 ? guaranteed : available;

    if (pool.length === 0) {
      throw new Error('NO_MATCHING_CARD');
    }

    return cloneCard(weightedPick(pool));
  }

  async pullById(id: string): Promise<CardData> {
    const card = allFriendCards.find((candidate) => candidate.id === id);
    if (!card) {
      throw new Error('CARD_NOT_FOUND');
    }

    return cloneCard(card);
  }

  async search(query: string): Promise<CardData[]> {
    const normalizedQuery = query.trim().toLowerCase();
    const matches = normalizedQuery
      ? allFriendCards.filter((card) =>
          [
            card.id,
            card.name,
            card.subtitle ?? '',
            card.description,
            ...card.tags,
            ...friendPacks.map((pack) => `${pack.name} ${pack.description}`)
          ]
            .join(' ')
            .toLowerCase()
            .includes(normalizedQuery)
        )
      : allFriendCards;

    return matches.slice(0, 10).map(cloneCard);
  }

  async prefetch(_count: number): Promise<void> {
    return Promise.resolve();
  }
}

function weightedPick(cards: CardData[]): CardData {
  const totalWeight = cards.reduce((total, card) => total + RARITY_WEIGHTS[card.rarity], 0);
  let roll = Math.random() * totalWeight;

  for (const card of cards) {
    roll -= RARITY_WEIGHTS[card.rarity];
    if (roll <= 0) {
      return card;
    }
  }

  return cards[cards.length - 1];
}

function cloneCard(card: CardData): CardData {
  return {
    ...card,
    tags: [...card.tags],
    stats: card.stats.map((stat) => ({ ...stat })),
    metadata: { ...card.metadata }
  };
}
